import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { actions as topikActions } from "../../../../modules/topik";
import { Card } from "../../../../components";
import { useTopikMotion } from "../topikMotion";
import { commitHaptic } from "../haptics";
import { submitErrorKey } from "../submitError";
import ScrollCue from "../ScrollCue";
import TopikGeneralityStep from "./TopikGeneralityStep";
import TopikOutcomeStep from "./TopikOutcomeStep";

// Owns the frame (title/instruction/context card/error) + submit dispatch; delegates the
// distinct Q1 (scale) and Q2 (box) interactions to the two step components (§12.2).
const TopikComparisonView = ({ t, comparison: liveComparison, chosenPositiveCard, topikStatus, dispatch }) => {
  // Comparisons move on the snappy "doing" clock (§1.2, A9).
  const { quick, step } = useTopikMotion();
  // Freeze the comparison this keyed instance was mounted for. Each comparison gets its own
  // AnimatePresence key, so an instance only ever represents one comparison - but while it is
  // *exiting* the store's currentComparison has already advanced to the next comparison (or
  // cleared to null on entering the analysis phase). Re-reading the live value here would make
  // the outgoing view flash the successor's content, or crash outright on the null. Capturing
  // once on mount keeps each exiting view rendering its own comparison to completion.
  const [comparison] = useState(liveComparison);
  const { polarity, slot, question, cardA, cardB } = comparison;
  const ns = `topik-comparison-view.${polarity}.${slot === "final" ? "final" : question}`;
  // E1: the "Turn" relief line at the negative->positive seam - a non-blocking beat shown once,
  // on the first helpful comparison, acknowledging the difficult half is behind them.
  const showSeamRelief = polarity === "positive" && slot === "pair_1" && question === "generality";
  const viewClass = "topik-view topik-comparison-view "
    + `topik-comparison-view--${polarity} topik-comparison-view--${question}`;

  const submitGenerality = (cardId) => {
    commitHaptic();
    dispatch(topikActions.comparisons.generality.request({ comparisonId: comparison.id, cardId }));
  };

  const submitOutcome = (cardId) => {
    commitHaptic();
    dispatch(topikActions.comparisons.outcome.request({ comparisonId: comparison.id, cardId }));
  };

  return (
    <>
      <Helmet>
        <meta name="theme-color" content="#222222" />
        <html className="card-background" />
      </Helmet>
      <motion.main
        className={viewClass}
        variants={step}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={quick}
      >
        {showSeamRelief && (
          <p className="topik-comparison-view__seam-relief">
            {t("topik-comparison-view.seam-relief")}
          </p>
        )}
        <h1 className="topik-view__title">{t(`${ns}.title`)}</h1>
        <p className="topik-view__instruction">{t(`${ns}.instruction`)}</p>

        {polarity !== "positive" && chosenPositiveCard && (
          <div className="topik-view__context-card topik-view__context-card--positive">
            <span className="topik-view__context-label">{t(`${ns}.your-positive-choice`)}</span>
            <div className="topik-view__context-card-inner">
              <Card
                cardId={`ctx-${chosenPositiveCard.id}`}
                title={chosenPositiveCard.title}
                description={chosenPositiveCard.description || ""}
                suit="positive"
                isDiscarded
              />
              <CheckCircleIcon className="topik-view__chosen-badge" aria-hidden="true" />
            </div>
          </div>
        )}

        {question === "generality" ? (
          <TopikGeneralityStep
            t={t}
            polarity={polarity}
            cardA={cardA}
            cardB={cardB}
            submitting={topikStatus.submitting}
            onConfirm={submitGenerality}
          />
        ) : (
          <TopikOutcomeStep
            t={t}
            polarity={polarity}
            slot={slot}
            boxLabel={t(`${ns}.box-label`)}
            cardA={cardA}
            cardB={cardB}
            submitting={topikStatus.submitting}
            onConfirm={submitOutcome}
          />
        )}

        {topikStatus.error && (
          <p className="topik-view__error" role="alert">{t(submitErrorKey(topikStatus.error))}</p>
        )}
        <ScrollCue />
      </motion.main>
    </>
  );
};

TopikComparisonView.propTypes = {
  t: PropTypes.func.isRequired,
  // Not `.isRequired`: while this view animates out, the store's currentComparison has already
  // cleared to null, so the still-subscribed exiting instance re-renders with a null prop (it
  // renders from the value it froze on mount). Requiring it would log a spurious warning then.
  comparison: PropTypes.shape({
    id: PropTypes.string.isRequired,
    polarity: PropTypes.oneOf(["negative", "positive"]).isRequired,
    slot: PropTypes.oneOf(["pair_1", "pair_2", "final"]).isRequired,
    question: PropTypes.oneOf(["generality", "outcome"]).isRequired,
    cardA: PropTypes.object.isRequired,
    cardB: PropTypes.object.isRequired,
  }),
  chosenPositiveCard: PropTypes.object,
  topikStatus: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

TopikComparisonView.defaultProps = {
  comparison: null,
  chosenPositiveCard: null,
};

const mapStateToProps = (state) => ({
  comparison: state.topik.session.currentComparison,
  chosenPositiveCard: state.topik.session.chosenPositiveCard,
  topikStatus: state.topik.status,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikComparisonView);
